/**
 * Persist an AI-compiled website DSL as a REAL builder site.
 *
 * Turns the compiler's per-page Craft trees into the website-builder's canonical
 * persistence shape and writes it through the NORMAL handlers (which bump the
 * bootstrap version + purge edge cache on edits — never write Mongo directly,
 * migration plan §0b.1):
 *   1. each page tree → compressed `websiteJson` (await compressToBase64, lzutf8)
 *   2. shared chrome → appBar/footer with nav links via buildMetaSectionsWithLinks
 *   3. flat theme (strict-6-hex palette on create) + langs + ALWAYS the auth
 *      copies (defaultRegisterJson/defaultLoginJson, §0b.5)
 *   4. createWebsiteConfig → read back version → optional publish
 *
 * React-free: the two translation functions are passed in by the caller.
 */
import { compressToBase64, decompressFromBase64 } from "@/lib/utils/compression";
import {
  createWebsiteConfig,
  updateWebsiteConfigAndPage,
  toggleWebsiteStatus,
  getWebsiteConfig,
  websiteConfigExists,
} from "@/lib/api/handlers/WebsiteConfigHandling";
import { initializeOverview } from "@/lib/api/handlers/PageHandling";
import { isServerErr, type ServerError } from "@/lib/api/errors/ErrorHandler";
import { hasWebsiteContent } from "@/lib/website/websiteShell";
import type { WebsiteConfig } from "@/types/WebsiteConfig";
import type { Page } from "@/types/Page";
import type { WebsiteThemeId } from "@/types/WebsiteTheme";
import {
  buildMetaSectionsWithLinks,
  generateDefaultAuthTemplates,
} from "@/components/builders/website/configurator-panel/components/themes";
import { A } from "@/components/builders/website/configurator-panel/components/temp";
import type { CompiledPage } from "./buildWebsiteCraftJsFromBlocks";
import type { WebsiteThemeBlock } from "./websiteBlocksDsl";
import { buildThemeConfig, isPreset } from "./websiteThemeConfig";

export { buildThemeConfig } from "./websiteThemeConfig";

type TranslationFn = (key: string) => string;

type NavLink = { label: string; url: string };

/**
 * Inject nav links for the AI's OWN pages into the shared chrome.
 * buildMetaSectionsWithLinks only knows the builder's predefined content pages
 * (PAGE_DEFINITIONS) — it can't see arbitrary AI slugs — so for a multi-page AI
 * site we decompress the base appBar/footer, splice in [Home, ...extra pages],
 * and recompress (lzutf8, await). No-op for a single-page site.
 */
async function injectNav(
  appBarB64: string,
  footerB64: string,
  extraPages: NavLink[],
  groupTitle: string,
): Promise<[string, string]> {
  if (extraPages.length === 0) return [appBarB64, footerB64];
  const ab = JSON.parse(await decompressFromBase64(appBarB64)) as { props: Record<string, unknown> };
  // MERGE, don't clobber: a preset theme's appBar already carries baked nav
  // (section anchors). Append the AI's extra pages; only synthesize a Home link
  // when the base nav was empty (the legacy/no-preset appBar).
  const abLinks = (ab.props.links as NavLink[] | undefined) ?? [];
  ab.props.links = abLinks.length > 0 ? [...abLinks, ...extraPages] : [{ label: "Home", url: "/" }, ...extraPages];
  ab.props.isLinksVisible = true;
  const ft = JSON.parse(await decompressFromBase64(footerB64)) as { props: Record<string, unknown> };
  const ftGroups = (ft.props.linkGroups as { title: string; links: NavLink[] }[] | undefined) ?? [];
  if (ftGroups.length > 0) {
    ftGroups[0] = { ...ftGroups[0], links: [...(ftGroups[0].links ?? []), ...extraPages] };
    ft.props.linkGroups = ftGroups;
  } else {
    ft.props.linkGroups = [{ title: groupTitle, links: [{ label: "Home", url: "/" }, ...extraPages] }];
  }
  ft.props.isLinkGroupsVisible = true;
  return [await compressToBase64(JSON.stringify(ab)), await compressToBase64(JSON.stringify(ft))];
}

export type PersistResult =
  | { ok: true; websiteConfigId: string; published: boolean }
  | { ok: false; error: string };

export type PersistArgs = {
  eventId: string;
  pages: CompiledPage[];
  theme?: WebsiteThemeBlock;
  primaryLanguage?: string;
  languages?: string[];
  /** WebsiteThemeId driving appBar/footer chrome (nav + logo). */
  themeId?: string;
  /** also flip published=true after create (default false → draft) */
  publish?: boolean;
  t: TranslationFn; // "websiteBuilder"
  td: TranslationFn; // "websiteBuilderDefaults"
};

/**
 * Does the event already have a WEBSITE — not merely a config row?
 *
 * A page-less shell without an uploaded site (the settings gear's
 * `POST /ensure`, a deleted custom upload) is no website: the backend's create
 * adopts it, exactly as for the template configurator. Refusing on the row
 * alone made one gear click end AI website generation for the event
 * (ship-wave audit r1, S-1). An unreadable overview keeps the strict guard —
 * never a create against a site we merely failed to read (same as the
 * configurator).
 */
async function websiteAlreadyExists(eventId: string): Promise<boolean> {
  const rowExists = await websiteConfigExists(eventId)
    .then((e) => e === true)
    .catch(() => false);
  if (!rowExists) return false;
  const overview = await initializeOverview(eventId).catch(() => undefined);
  if (!overview || isServerErr(overview)) return true;
  return hasWebsiteContent(overview.pages, overview.metaPage);
}

/**
 * Assemble + persist. Returns a structured result; never throws on a handler
 * ServerError (the caller surfaces it to the model / UI).
 */
export async function persistWebsiteDraft(args: PersistArgs): Promise<PersistResult> {
  const { eventId, pages, t, td } = args;
  if (await websiteAlreadyExists(eventId)) {
    // First cut creates only; editing an existing AI site is the Phase 4
    // in-builder lane. Surface honestly rather than clobbering.
    return { ok: false, error: "websiteAlreadyExists" };
  }

  // 1. page trees → compressed Page rows (active:true so they're reachable).
  const persistPages: Page[] = [];
  for (const p of pages) {
    persistPages.push({
      pageName: p.pageName,
      slug: p.slug,
      active: true,
      websiteJson: await compressToBase64(JSON.stringify(p.tree)),
    });
  }

  // 2. shared chrome with nav links (NOT empty — §0b.4). Start from the builder's
  //    base chrome, then splice in the AI's own extra pages for the nav.
  const themeId = isPreset(args.themeId) ? (args.themeId as WebsiteThemeId) : undefined;
  const [baseAppBar, baseFooter] = buildMetaSectionsWithLinks(td, t, [], themeId);
  const extraNav: NavLink[] = pages
    .filter((p) => p.slug !== "/")
    .map((p) => ({ label: p.pageName, url: p.slug }));
  const [navAppBar, footer] = await injectNav(baseAppBar, baseFooter, extraNav, t("company"));
  let appBar = navAppBar;

  // theme.appBarTransparent → AppBar node `transparent: true` (over-hero mode).
  // Absent/false leaves the chrome untouched (byte-identical to pre-WS2 output).
  if (args.theme?.appBarTransparent === true) {
    const ab = JSON.parse(await decompressFromBase64(appBar)) as { props: Record<string, unknown> };
    ab.props.transparent = true;
    appBar = await compressToBase64(JSON.stringify(ab));
  }

  // 3. flat theme + langs + ALWAYS the auth copies (§0b.5).
  const authTemplates = generateDefaultAuthTemplates(t, td);
  const config: WebsiteConfig = {
    ...buildThemeConfig(args.theme),
    appBar,
    footer,
    homeSlug: "/",
    primaryLanguage: args.primaryLanguage || "de-DE",
    languages: args.languages ?? [],
    favIcon: `${A}/shared/favicon-512x512.png`,
    ...authTemplates,
  };

  // 4. one-shot create.
  // Art. 50 KI-VO: the AI authored this site — the server stamps
  // website_configs.aiGenerated (createdByAi) and the guest pages carry
  // <meta name="ai-disclosure" content="ai-generated">.
  const created = await createWebsiteConfig(eventId, config, persistPages, "generate_website_from_blocks");
  if (isServerErr(created)) return { ok: false, error: (created as ServerError).message };
  const websiteConfigId = created as string;

  if (!args.publish) return { ok: true, websiteConfigId, published: false };

  const toggled = await toggleWebsiteStatus(eventId, true);
  if (isServerErr(toggled)) {
    // Created but not published — report honestly so the user can publish in UI.
    return { ok: true, websiteConfigId, published: false };
  }
  return { ok: true, websiteConfigId, published: true };
}

/** Re-fetch the created config to capture `version` before any update
 *  (If-Match → 409 on stale). Exposed for the Phase 4 edit lane. */
export async function readConfigVersion(eventId: string): Promise<number | undefined> {
  const cfg = await getWebsiteConfig(eventId, "version");
  if (!cfg || isServerErr(cfg)) return undefined;
  return (cfg as WebsiteConfig).version;
}

/** Update a single page + config on an existing AI site (Phase 4 edit lane).
 *  Routes through updateWebsiteConfigAndPage so appBar/footer re-bundle and the
 *  bootstrap version bumps + cache purges (§0b.1). */
export async function updateWebsitePage(
  eventId: string,
  config: WebsiteConfig,
  page: Page,
): Promise<PersistResult> {
  // Art. 50 KI-VO: this lane only ever writes AI output.
  const res = await updateWebsiteConfigAndPage(eventId, config, page, "generate_website_from_blocks");
  if (isServerErr(res)) return { ok: false, error: (res as ServerError).message };
  return { ok: true, websiteConfigId: config.websiteConfigId ?? "", published: !!config.published };
}
